/**
 * 606 Meta5 AI Trading - News Tab (AI-powered)
 *
 * Pulls AI-curated news affecting Gold (XAU), Oil (USOIL) and USD via
 * Gemini's Google Search grounding. Falls back to the legacy feed.
 *
 * Each item shows:
 *  - Title + summary in BOTH Arabic and English
 *  - Per-instrument impact badges (Gold / Oil / USD)
 *  - Importance (high / medium / low)
 *  - Source publisher + link
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Newspaper, Sparkles, Loader2, ExternalLink,
  TrendingUp, TrendingDown, Minus, Globe,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTrading } from '../../lib/trading-i18n';
import { fetchNews, refreshNewsAI } from '../../lib/trading-api';
import { Button } from '../../components/ui/button';

const NewsTab = ({ language = 'ar' }) => {
  const t = useTrading(language);
  const isAr = language === 'ar';
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastSource, setLastSource] = useState(null);

  const loadNews = useCallback(async () => {
    try {
      const data = await fetchNews(20);
      setNews(data.data || []);
    } catch (err) {
      console.error('Failed to load news:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadNews();
  }, [loadNews]);

  const handleRefreshAI = async () => {
    setRefreshing(true);
    try {
      const res = await refreshNewsAI();
      setLastSource(res.source);
      await loadNews();
      if (res.warning) {
        toast.warning(res.warning);
      } else {
        toast.success(
          isAr
            ? `✨ تم تحليل ${res.count} خبر بواسطة Gemini`
            : `✨ ${res.count} news items analyzed by Gemini`
        );
      }
    } catch (err) {
      console.error('AI news refresh failed:', err);
      toast.error(
        err?.response?.data?.detail
          || (isAr ? 'فشل تحديث الأخبار بالذكاء الاصطناعي' : 'AI news refresh failed')
      );
    }
    setRefreshing(false);
  };

  const formatTime = (isoString) => {
    try {
      const date = new Date(isoString);
      const now = new Date();
      const diff = (now - date) / 1000 / 60; // minutes
      if (diff < 60) return isAr ? `قبل ${Math.round(diff)} د` : `${Math.round(diff)}m ago`;
      if (diff < 1440) return isAr ? `قبل ${Math.round(diff / 60)} س` : `${Math.round(diff / 60)}h ago`;
      return date.toLocaleDateString(isAr ? 'ar-AE' : 'en-US');
    } catch {
      return '--';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="news-loading">
        <div className="w-8 h-8 border-2 border-[#d4af37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="news-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Newspaper className="h-5 w-5 text-[#d4af37]" />
            {isAr ? 'الأخبار المالية' : 'Financial News'}
          </h3>
          <p className="text-[11px] text-[#71717A] mt-0.5">
            {isAr
              ? 'تحليل ذكي لتأثير كل خبر على الذهب • البترول • الدولار'
              : 'AI-powered impact on Gold • Oil • USD'}
            {lastSource && (
              <span className="ms-2 text-[10px] text-[#d4af37]/70">
                · {lastSource === 'gemini_search' ? 'Gemini Search' : lastSource}
              </span>
            )}
          </p>
        </div>
        <Button
          onClick={handleRefreshAI}
          disabled={refreshing}
          className="bg-gradient-to-r from-[#d4af37] to-[#9b7d20] hover:opacity-90 text-black font-semibold shadow-lg shadow-[#d4af37]/20"
          data-testid="refresh-news-ai-btn"
        >
          {refreshing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin me-2" />
              {isAr ? 'جاري تحليل الأخبار...' : 'Analyzing news...'}
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 me-2" />
              {isAr ? 'تحديث الأخبار بالذكاء الاصطناعي' : 'Refresh with AI'}
            </>
          )}
        </Button>
      </div>

      {/* News Grid */}
      {news.length === 0 ? (
        <div className="text-center py-12 text-[#71717A]" data-testid="news-empty">
          {isAr
            ? 'لا توجد أخبار بعد — اضغط "تحديث الأخبار بالذكاء الاصطناعي" للبدء'
            : 'No news yet — click "Refresh with AI" to fetch'}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {news.map((item, idx) => (
            <NewsCard key={item.id} item={item} idx={idx} isAr={isAr} formatTime={formatTime} />
          ))}
        </div>
      )}
    </div>
  );
};

// ───── Single news card ─────────────────────────────────────────────
function NewsCard({ item, idx, isAr, formatTime }) {
  const titleAr = item.title_ar || item.title || '';
  const titleEn = item.title_en || item.title || '';
  const summaryAr = item.summary_ar || item.summary || '';
  const summaryEn = item.summary_en || item.summary || '';
  const isAiPowered = Boolean(item.ai_powered);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(idx * 0.04, 0.4) }}
      className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-4 hover:border-[#d4af37]/30 transition-colors"
      data-testid={`news-card-${item.id}`}
    >
      {/* Top row: importance + time + source */}
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ImportanceBadge value={item.impact} isAr={isAr} />
          {isAiPowered && (
            <span
              className="text-[10px] px-2 py-0.5 rounded-full bg-[#d4af37]/15 text-[#d4af37] border border-[#d4af37]/30 flex items-center gap-1"
              data-testid="news-ai-tag"
            >
              <Sparkles className="h-3 w-3" />
              AI
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-[10px] text-[#71717A]">
          {item.source && (
            <span className="flex items-center gap-1">
              <Globe className="h-3 w-3" />
              {item.source}
            </span>
          )}
          <span>·</span>
          <span>{formatTime(item.timestamp)}</span>
        </div>
      </div>

      {/* Arabic side */}
      {titleAr && (
        <div className="mb-3" dir="rtl">
          <h4 className="text-white font-semibold text-sm mb-1 leading-relaxed">{titleAr}</h4>
          {summaryAr && (
            <p className="text-white/60 text-xs leading-relaxed">{summaryAr}</p>
          )}
        </div>
      )}

      {/* English side */}
      {titleEn && titleEn !== titleAr && (
        <div className="mb-3 pt-3 border-t border-white/5" dir="ltr">
          <h4 className="text-white/90 font-medium text-sm mb-1 leading-relaxed">{titleEn}</h4>
          {summaryEn && summaryEn !== summaryAr && (
            <p className="text-white/50 text-xs leading-relaxed">{summaryEn}</p>
          )}
        </div>
      )}

      {/* Per-instrument impact row */}
      <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-3 gap-2">
        <ImpactPill
          label={isAr ? 'الذهب' : 'Gold'}
          symbol="XAU"
          value={item.impact_gold}
          isAr={isAr}
          testId={`impact-gold-${item.id}`}
        />
        <ImpactPill
          label={isAr ? 'البترول' : 'Oil'}
          symbol="OIL"
          value={item.impact_oil}
          isAr={isAr}
          testId={`impact-oil-${item.id}`}
        />
        <ImpactPill
          label={isAr ? 'الدولار' : 'USD'}
          symbol="DXY"
          value={item.impact_usd}
          isAr={isAr}
          testId={`impact-usd-${item.id}`}
        />
      </div>

      {/* Source link */}
      {item.source_url && (
        <a
          href={item.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1 text-[11px] text-[#d4af37] hover:text-[#e8c356] transition-colors"
          data-testid={`news-source-link-${item.id}`}
        >
          <ExternalLink className="h-3 w-3" />
          {isAr ? 'فتح المصدر' : 'Open source'}
        </a>
      )}
    </motion.div>
  );
}

// ───── Importance badge (high / medium / low) ────────────────────────
function ImportanceBadge({ value, isAr }) {
  const v = (value || 'medium').toLowerCase();
  const map = {
    high:   { ar: 'تأثير عالي',    en: 'High impact',   bg: 'bg-red-500/15',    border: 'border-red-500/40',    text: 'text-red-300' },
    medium: { ar: 'تأثير متوسط',  en: 'Medium impact', bg: 'bg-amber-500/15',  border: 'border-amber-500/40',  text: 'text-amber-300' },
    low:    { ar: 'تأثير منخفض',  en: 'Low impact',    bg: 'bg-zinc-500/15',   border: 'border-zinc-500/40',   text: 'text-zinc-300' },
  };
  const c = map[v] || map.medium;
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${c.bg} ${c.border} ${c.text}`}>
      {isAr ? c.ar : c.en}
    </span>
  );
}

// ───── Per-instrument impact pill ────────────────────────────────────
function ImpactPill({ label, symbol, value, isAr, testId }) {
  const v = (value || 'neutral').toLowerCase();
  const map = {
    bullish: {
      ar: 'صاعد', en: 'Bullish',
      bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-300',
      Icon: TrendingUp,
    },
    bearish: {
      ar: 'هابط', en: 'Bearish',
      bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-300',
      Icon: TrendingDown,
    },
    neutral: {
      ar: 'محايد', en: 'Neutral',
      bg: 'bg-zinc-500/10', border: 'border-zinc-500/30', text: 'text-zinc-300',
      Icon: Minus,
    },
  };
  const c = map[v] || map.neutral;
  const Icon = c.Icon;
  return (
    <div
      className={`flex flex-col items-center gap-0.5 rounded-lg border ${c.bg} ${c.border} py-1.5 px-2`}
      data-testid={testId}
    >
      <span className="text-[10px] text-white/50 font-medium">{label}</span>
      <span className={`flex items-center gap-1 text-[11px] font-semibold ${c.text}`}>
        <Icon className="h-3 w-3" />
        {isAr ? c.ar : c.en}
      </span>
    </div>
  );
}

export default NewsTab;
